import { formatScore } from "../../../pearliCore/assessment";
import React from "react";
import { Header } from "../Header-Assesment/Header";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRotateRight,
  faFileAudio,
} from "@fortawesome/free-solid-svg-icons";
import { Chart } from "../Graphing/Chart";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { clearAssessmentId } from "../../../store/authSlice.js";
import SealCheck from "../../../assets/SealCheck.svg";
import Markdown from "react-markdown";
import AudioPlayer from "react-h5-audio-player";
import "react-h5-audio-player/lib/styles.css";
import "./AssessmentTemplateAudio.css";
import "./ReportExperience.css";
import AIDisclaimer from "../../../components/common/AIDisclaimer";

const recordingUrl = "https://storage.googleapis.com/mindsherpa-app-bucket/audios/PEARLI.mp3";

export const AssessmentTemplate = ({
  headerTitle,
  headerSubtitle,
  breadcrumbs,
  tags,
  title,
  subtitle,
  scores,
  summaries,
  overAll,
  complete = true,
  reportNotice,
  onRetry,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  /**
   * Handle retake assessment - clear Redux/localStorage and navigate to Disclaimer
   * This ensures the user goes through the full flow and a new assessment is created
   */
  const handleRetakeAssessment = () => {
    dispatch(clearAssessmentId());
    navigate("/Disclaimer");
  };


  const avg =
    complete && Array.isArray(scores) && scores.length === 6 && scores.every((score) => typeof score === "number" && score >= 1)
      ? (
        (scores[0] +
          scores[1] +
          scores[2] +
          scores[3] +
          scores[4] +
          scores[5]) /
        6
      ).toFixed(1)
      : null;

  return (
    <div className="mainReport">
      <div className="header-assessment">
        <Header title={headerTitle} subtitle={headerSubtitle} />
      </div>

      <div className="toppart">
        <div className="breadcrumb">
          {breadcrumbs.map((crumb, i) => (
            <React.Fragment key={i}>
              {crumb.icon && (
                <FontAwesomeIcon icon={crumb.icon} style={{ color: "#c1c0bd" }} />
              )}
              <p className={crumb.active ? "element-text3" : "element-text"}>
                {crumb.text}
              </p>
              {i !== breadcrumbs.length - 1 && <p className="overline">/</p>}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="mainpart">
        <div className="assessments-reports">
          <div className="frame-1">
            {tags.map((tag, i) => (
              <p className="bar-tab" key={i}>
                <span className="bar-tab-text">
                  {typeof scores[i] === "number" && scores[i] >= 1 && (
                    <img src={SealCheck} alt="Completed" className="check-icon-inline" />
                  )}
                  {tag}
                </span>
              </p>
            ))}
          </div>

          <div className="frame-2">
            <p className="title-main">{title}</p>
            <p className="subtitle-main">{subtitle}</p>
          </div>

          <div className="Report-frame-3">
            {/* LEFT column: Chart + Average */}
            <div style={{ display: "flex", flexDirection: "column" }}>
              {avg !== null ? <Chart scores={scores} /> : (
                <p>Complete all six sections to view the full comparison chart.</p>
              )}

              <div className="textAverageScore" style={{ marginTop: 8, textAlign: "center" }}>
                {avg !== null ? `Average Score: ${avg}/5.0` : "Overall score available after all six sections"}
              </div>

            </div>

            {/* RIGHT column: Overall + Details */}
            <div className="starDisplay">
              <div className="starBox">
                <h2 className="report-section-title">What stands out</h2>
                {reportNotice && <p role="status">{reportNotice}</p>}
                <Markdown children={overAll} />
                {onRetry && <button type="button" onClick={onRetry}>Try again</button>}
                <section id="pearli-guidance" className="report-guidance" aria-labelledby="pearli-guidance-title">
                  <h3 id="pearli-guidance-title">
                    <FontAwesomeIcon icon={faFileAudio} /> Listen to your PEARLI guidance
                  </h3>
                  <p>About 6 minutes · A guided pause to reflect on the six areas of well-being.</p>
                  <div className="assessment-audio-player">
                    <div className="assessment-audio-player-inner">
                      <AudioPlayer
                        src={recordingUrl}
                        autoPlay={false}
                        autoPlayAfterSrcChange={false}
                        preload="none"
                        i18nAriaLabels={{ ...AudioPlayer.defaultI18nAriaLabels, player: "PEARLI well-being guidance recording" }}
                        progressJumpSteps={{ backward: 10000, forward: 10000 }}
                      />
                    </div>
                  </div>
                </section>
              </div>
            </div>

            <section className="scoreBox" aria-label="Your six domains">
              <h2 className="report-section-title">Your answers, in context</h2>
              <div className="scoreView">
                {["Physical", "Emotional", "Acceptance (financial wellbeing)", "Relationships", "Loving Kindness", "Intellectual"].map((label, index) => (
                  <details className="report-domain" key={label} open={typeof scores[index] === "number" && scores[index] <= 2}>
                    <summary>
                      <span>{label}</span>
                      <span className={"score c" + (scores[index] ? Math.round(scores[index]) : "")}>{formatScore(scores[index])}</span>
                    </summary>
                    <div className="contentReport"><Markdown>{summaries[index]}</Markdown></div>
                  </details>
                ))}
              </div>
            </section>
          </div>

          <div style={{ width: "100%", maxWidth: "100%", marginTop: "16px", marginBottom: "8px" }}>
            <AIDisclaimer
              variant="banner"
              title="AI Assessment Insights Disclaimer"
              text="These reflections and summary points are generated using artificial intelligence based on your PEARLI assessment scores to support personal self-reflection. They do not constitute medical, psychological, or psychiatric advice."
            />
          </div>

          <div className="Report-frame-4">
            <button
              className="explore"
              type="button"
              onClick={() => navigate("/mindfulness")}
            >
              Explore Mindfulness
            </button>
            <button
              className="retake"
              type="button"
              onClick={handleRetakeAssessment}
            >
              <FontAwesomeIcon icon={faRotateRight} />
              &nbsp;&nbsp;Retake Assessment
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
